import { IAnalysisUseCase } from './i-analysis-use-case'
import { IQueryRepository } from '../repositories/i-query-repository'
import { LayerEntity } from '../models/entities/layer-entity'
import { AnalysisResultEntity } from '../models/entities/analysis-result-entity'
import { LayerQueryResultEntity } from '../models/entities/layer-query-result-entity'
import { ResponseValueObject } from '../models/value-objects/response-value-object'
import { StatisticDefinition } from '../models/entities/statistic-definition-entity'
import { StatisticTypeEnum } from '../models/enums/statistic-type-enum'
import { LayerStatisticEntity } from '../models/entities/layer-statistic-entity'
import { ExtentEntity } from '../models/entities/extent-entity'

/**
 * Ámbito territorial activo de un análisis de cruce.
 *
 * - `code`: código ubigeo más específico disponible (distrito > provincia >
 *   departamento). Las capas con filtro prefijo (`id_dist LIKE '{0}%'`)
 *   funcionan con cualquier nivel porque el código es jerárquico.
 * - `departmentName`: nombre del departamento activo, para las capas que
 *   filtran por nombre (`UPPER(nom_dpto) = UPPER('{0}')`). La etiqueta
 *   'Nacional' NO es un filtro válido y se normaliza a null.
 * - `geometry`: geometría real del ubigeo activo (esriJSON/Polygon, en el SR
 *   del mapa). Define el área de análisis del cruce espacial.
 */
export interface AnalysisScope {
  code: string | null
  departmentName: string | null
  label: string
  geometry: unknown
}

/**
 * Base común de los motores de intersección real (cruce territorial).
 *
 * Robustez:
 * - Área de análisis en capas: geometría activa del ubigeo → extensión de la
 *   capa base → null (cruce espacial omitido). Nunca lanza por falta de área.
 * - Aislamiento por capa: un servicio caído degrada SOLO esa capa
 *   (LayerQueryResultEntity.error) y no tira el análisis completo.
 * - Anti 'Nacional': la etiqueta del selector no se filtra como departamento.
 * - SR explícito: el envelope derivado de la geometría conserva su
 *   spatialReference y el repositorio lo usa como inSR.
 */
export abstract class BaseCrossAnalysisUseCase implements IAnalysisUseCase {
  abstract readonly id: string
  abstract readonly name: string

  private static readonly GROUP_STAT: StatisticDefinition[] = [{
    statisticType: StatisticTypeEnum.Count,
    onStatisticField: '1',
    outStatisticFieldName: 'count',
  }]

  constructor(protected readonly queryRepo: IQueryRepository) {}

  async execute(
    layers: LayerEntity[],
    departmentCode?: string | null,
    departmentName?: string | null,
    scopeGeometry?: unknown,
  ): Promise<ResponseValueObject<AnalysisResultEntity>> {
    const scope: AnalysisScope = {
      code: this.cleanText(departmentCode),
      departmentName: this.cleanDepartmentName(departmentName),
      label: this.cleanDepartmentName(departmentName) ?? this.cleanText(departmentCode) ?? 'Nacional',
      geometry: scopeGeometry ?? null,
    }

    const errors: string[] = []

    // Capa base: la primera filtrable (definitionQuery o fieldQuery por
    // código); si no hay ámbito o ninguna es filtrable, la primera capa
    // (su extensión sirve de área de análisis).
    const filterableIdx = layers.findIndex(
      l => !!(scope.code && (l.definitionQuery || l.fieldQuery)),
    )
    const baseIdx = filterableIdx >= 0 ? filterableIdx : 0
    const baseLayer = layers[baseIdx]

    // 1) Área de análisis: geometría activa del ubigeo → extensión de la capa base.
    let baseExtent: ExtentEntity | null = this.envelopeFromGeometry(scope.geometry)
    if (!baseExtent && baseLayer) {
      const extentRes = await this.queryRepo.queryLayerExtent(baseLayer.url ?? '', this.buildWhere(baseLayer, scope))
      if (extentRes.success) {
        baseExtent = extentRes.data ?? null
      } else {
        errors.push(`Área de análisis no disponible: ${extentRes.message ?? 'error de servicio'}`)
      }
    }

    // 2) Cruce por capa, aislado: un servicio caído no tira el análisis completo.
    const settled = await Promise.allSettled(
      layers.map(async (layer, i): Promise<LayerQueryResultEntity> => {
        const url = layer.url ?? ''
        const where = this.buildWhere(layer, scope)

        const totalRes = await this.queryRepo.queryLayerCount(url, where)
        if (!totalRes.success) {
          throw new Error(totalRes.message ?? 'servicio no disponible')
        }
        const featureCount = totalRes.data ?? 0

        const layerWarnings: string[] = []

        let categoryStats: LayerStatisticEntity[] | undefined
        if (layer.categoryField) {
          const catRes = await this.queryRepo.queryLayerStatistics(
            url,
            where,
            BaseCrossAnalysisUseCase.GROUP_STAT,
            [layer.categoryField],
          )
          if (catRes.success) {
            categoryStats = catRes.data ?? undefined
          } else {
            layerWarnings.push(`categorías no disponibles (${catRes.message ?? 'error'})`)
          }
        }

        let spatialCrossCount: number | undefined
        if (!baseLayer || i === baseIdx || !baseExtent) {
          // Sin área de análisis medible o capa base: el cruce no aplica.
          spatialCrossCount = featureCount
        } else {
          const crossRes = await this.queryRepo.queryLayerCount(
            url,
            where,
            baseExtent,
            'esriGeometryEnvelope',
            'esriSpatialRelIntersects',
          )
          if (crossRes.success) {
            // undefined cuando el cruce falla: el panel NO lo inventa como 100%.
            spatialCrossCount = crossRes.data ?? 0
          } else {
            layerWarnings.push(`cruce espacial no disponible (${crossRes.message ?? 'error'})`)
          }
        }

        return {
          layerName: layer.name,
          layerUrl: url,
          featureCount,
          categoryStats,
          spatialCrossCount,
          error: layerWarnings.length > 0 ? layerWarnings.join(' · ') : null,
        }
      }),
    )

    const layerResults: LayerQueryResultEntity[] = settled.map((r, i) => {
      if (r.status === 'fulfilled') return r.value
      const layer = layers[i]
      const reason = r.reason instanceof Error ? r.reason.message : String(r.reason ?? 'error')
      errors.push(`Capa "${layer?.name ?? i + 1}": ${reason}`)
      return {
        layerName: layer?.name ?? `Capa ${i + 1}`,
        layerUrl: layer?.url ?? '',
        featureCount: 0,
        error: reason,
      }
    })

    if (settled.length > 0 && settled.every(r => r.status === 'rejected')) {
      return new ResponseValueObject(
        false,
        undefined as unknown as AnalysisResultEntity,
        'Ningún servicio de capas respondió. Verifica tu conexión e inténtalo nuevamente.',
        errors,
      )
    }

    return new ResponseValueObject(true, {
      analysisId: this.id,
      analysisName: this.name,
      completedAt: new Date(),
      layerResults,
      totalFeatures: layerResults.reduce((s, r) => s + (r.featureCount || 0), 0),
    }, undefined, errors)
  }

  /**
   * where de la capa según el ámbito:
   * - definitionQuery: filtro principal (prefijo de código por defecto; nombre
   *   del departamento si definitionQueryWithLabel). Úsalo como capa base.
   * - fieldQuery: filtro secundario por campo. Con fieldQueryWithLabel filtra
   *   por nombre del departamento (`UPPER(f) = UPPER('X')`); sin él, prefijo
   *   de código ubigeo (`f LIKE 'X%'`) — funciona con dpto, provincia o
   *   distrito porque el código es jerárquico.
   * Sin ámbito (nacional) o sin el dato que la capa necesita → '1=1'.
   */
  private buildWhere(entity: LayerEntity, scope: AnalysisScope): string {
    if (entity.definitionQuery) {
      const value = entity.definitionQueryWithLabel ? scope.departmentName : scope.code
      if (!value) return '1=1'
      const e = new LayerEntity(entity)
      e.setValue(value)
      return e.definitionQuery ?? '1=1'
    }
    if (entity.fieldQuery) {
      const value = this.fieldQueryValue(entity, scope)
      if (!value) return '1=1'
      const f = entity.fieldQuery.replace(/"/g, '""')
      return entity.fieldQueryWithLabel
        ? `UPPER(${f}) = UPPER('${scope.departmentName!.replace(/'/g, "''")}')`
        : `${f} LIKE '${value.replace(/'/g, "''")}%'`
    }
    return '1=1'
  }

  /** Valor que aplica el fieldQuery: nombre (con label) o código jerárquico. */
  private fieldQueryValue(entity: LayerEntity, scope: AnalysisScope): string | null {
    if (entity.fieldQueryWithLabel) return scope.departmentName
    // Jerárquico: usa el código más específico disponible (distrito > provincia > departamento).
    return scope.code
  }

  /**
   * Envelope equivalente a la geometría del ámbito. Acepta geometría esriJSON
   * (rings) u objetos con extent. Conserva el spatialReference para que el
   * repositorio consulte con el inSR correcto (no asume 4326).
   */
  private envelopeFromGeometry(geometry: unknown): ExtentEntity | null {
    if (!geometry || typeof geometry !== 'object') return null
    const g = geometry as {
      rings?: unknown
      extent?: any
      spatialReference?: { wkid?: number }
    }

    const rings = g.rings
    if (Array.isArray(rings) && rings.length > 0) {
      let xmin = Infinity, ymin = Infinity, xmax = -Infinity, ymax = -Infinity
      for (const ring of rings as unknown[]) {
        if (!Array.isArray(ring)) continue
        for (const pt of ring as unknown[]) {
          if (!Array.isArray(pt) || pt.length < 2) continue
          const x = pt[0]
          const y = pt[1]
          if (typeof x !== 'number' || typeof y !== 'number') continue
          if (!Number.isFinite(x) || !Number.isFinite(y)) continue
          if (x < xmin) xmin = x
          if (y < ymin) ymin = y
          if (x > xmax) xmax = x
          if (y > ymax) ymax = y
        }
      }
      if ([xmin, ymin, xmax, ymax].every(n => Number.isFinite(n))) {
        return {
          xmin, ymin, xmax, ymax,
          spatialReference: g.spatialReference as ExtentEntity['spatialReference'],
        }
      }
    }

    const ext = g.extent
    if (
      ext &&
      [ext.xmin, ext.ymin, ext.xmax, ext.ymax].every((n: unknown) => typeof n === 'number' && Number.isFinite(n))
    ) {
      return {
        xmin: ext.xmin,
        ymin: ext.ymin,
        xmax: ext.xmax,
        ymax: ext.ymax,
        spatialReference: (ext.spatialReference ?? g.spatialReference) as ExtentEntity['spatialReference'],
      }
    }

    return null
  }

  private cleanText(value: string | null | undefined): string | null {
    const v = (value ?? '').trim()
    return v.length > 0 ? v : null
  }

  /** 'Nacional' (etiqueta del selector) no es un departamento filtrable. */
  private cleanDepartmentName(value: string | null | undefined): string | null {
    const v = this.cleanText(value)
    return v && !/^nacional$/i.test(v) ? v : null
  }
}
